/**
 * Google One Tap on the sign-in page: "Continue as …" for the Google account
 * the browser is already signed into, with no window to open.
 *
 * Google Identity Services shows the prompt (when Google decides to: not
 * after it has been closed a few times, and not where the browser blocks it)
 * and hands back an ID token, which signs into Firebase as the Google button
 * does (AuthContext.signInWithGoogleIdToken). The client id is the app's own
 * Google client (VITE_GOOGLE_CLIENT_ID), which is in the Firebase project.
 *
 * Switched on and off in Grow Settings > Welcome (welcome.GOOGLE_ONE_TAP).
 * Nothing is drawn here: the prompt is Google's own.
 *
 * Used by: LoginPage only.
 *
 * @module GoogleOneTap
 */

import React from 'react';
import { VITE_GOOGLE_CLIENT_ID } from '@/config/env-vars';
import { useAuth } from '@/shared/hooks/useAuth';
import { markSigningIn } from '@/shared/lib/homePage';
import { debugLog } from '@/config/config';
import { GOOGLE_IDENTITY_SCRIPT_URL } from '@/config/tunables/shared';

/** Google Identity Services, which One Tap is part of (the same script Google Photos loads). */
const GSI_SRC = GOOGLE_IDENTITY_SCRIPT_URL;

/**
 * Loads Google Identity Services once.
 *
 * @returns A promise that settles when `google.accounts.id` is there.
 */
function loadIdentityServices(): Promise<void> {
  if (typeof google !== 'undefined' && google.accounts?.id) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
    const script = existing ?? document.createElement('script');
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => reject(new Error('Google sign-in could not be loaded')));
    if (!existing) {
      script.src = GSI_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
  });
}

/** What the sign-in page hears back from One Tap. */
interface GoogleOneTapProps {
  /** Said when a chosen account could not be signed in. */
  onError: (message: string) => void;
  /** Told when an account has been chosen and is being signed in. */
  onStart?: () => void;
}

/** Offers One Tap while the sign-in page is open, and takes it away after. */
export const GoogleOneTap: React.FC<GoogleOneTapProps> = ({ onError, onStart }) => {
  const { signInWithGoogleIdToken } = useAuth();
  // The latest handlers, without starting the prompt again when they change.
  const handlers = React.useRef({ signInWithGoogleIdToken, onError, onStart });
  handlers.current = { signInWithGoogleIdToken, onError, onStart };

  React.useEffect(() => {
    if (!VITE_GOOGLE_CLIENT_ID || import.meta.env.VITE_USE_MOCK_AUTH === 'true') return undefined;
    let gone = false;
    loadIdentityServices()
      .then(() => {
        if (gone) return;
        google.accounts.id.initialize({
          client_id: VITE_GOOGLE_CLIENT_ID,
          callback: ({ credential }) => {
            markSigningIn();
            handlers.current.onStart?.();
            handlers.current.signInWithGoogleIdToken(credential).catch((error: unknown) => {
              handlers.current.onError(error instanceof Error ? error.message : String(error));
            });
          },
          auto_select: false,
          cancel_on_tap_outside: true,
          context: 'signin',
          itp_support: true,
          use_fedcm_for_prompt: true,
        });
        google.accounts.id.prompt();
      })
      .catch((error) => debugLog('GoogleOneTap', 'One Tap not offered:', error));
    return () => {
      gone = true;
      if (typeof google !== 'undefined') google.accounts?.id?.cancel();
    };
  }, []);

  return null;
};
